import React, { HTMLAttributes, useCallback, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { Container, WeekDays, Days } from './styles';

const months = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface CalendarProps extends HTMLAttributes<HTMLDivElement> {
  defaultStartDate: Date;
  defaultEndDate: Date;
  setDate(startDate: Date, endDate: Date): void;
}

const Calendar: React.FC<CalendarProps> = ({
  defaultStartDate,
  defaultEndDate,
  setDate,
  ...props
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(
    defaultStartDate,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isSelectedStart, setIsSelectedStart] = useState(false);

  const handlePrevMonth = useCallback(() => {
    const date = new Date(currentDate);

    date.setMonth(currentDate.getMonth() - 1);

    setCurrentDate(date);
  }, [currentDate]);

  const handleNextMonth = useCallback(() => {
    const date = new Date(currentDate);

    date.setMonth(currentDate.getMonth() + 1);

    setCurrentDate(date);
  }, [currentDate]);

  const handleDayClick = useCallback(
    (date: Date): void => {
      if (
        isSelectedStart &&
        startDate &&
        date.getTime() < startDate.getTime()
      ) {
        setStartDate(new Date(date));
        setIsSelectedStart(true);

        return;
      }

      if (isSelectedStart && startDate) {
        setEndDate(new Date(date));
        setIsSelectedStart(false);
        setDate(startDate, new Date(date));

        return;
      }

      setStartDate(new Date(date));
      setEndDate(undefined);
      setIsSelectedStart(true);
    },
    [startDate, isSelectedStart, setDate],
  );

  function handleDayClasses(date: Date): string {
    const classes: string[] = [];

    if (
      startDate &&
      startDate.getDate() === date.getDate() &&
      startDate.getMonth() === date.getMonth() &&
      startDate.getFullYear() === date.getFullYear()
    ) {
      classes.push('start');
    }

    if (
      endDate &&
      endDate.getDate() === date.getDate() &&
      endDate.getMonth() === date.getMonth() &&
      endDate.getFullYear() === date.getFullYear()
    ) {
      classes.push('end');
    }

    if (
      new Date().getDate() === date.getDate() &&
      new Date().getMonth() === date.getMonth() &&
      new Date().getFullYear() === date.getFullYear()
    ) {
      classes.push('today');
    }

    if (
      startDate &&
      endDate &&
      date.getTime() > startDate.getTime() &&
      date.getTime() < endDate.getTime()
    ) {
      if (!classes.includes('start') && !classes.includes('end')) {
        classes.push('between');
      }
    }

    return classes.join(' ');
  }

  function renderDays(): React.ReactNode {
    const days: JSX.Element[] = [];

    const lastDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    );

    const firstDayIndex = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    ).getDay();
    const lastDayIndex = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    ).getDay();
    const prevLastDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      0,
    );

    for (let i = firstDayIndex; i > 0; i -= 1) {
      days.push(
        <button
          type="button"
          key={Math.random()}
          className={`different-month ${handleDayClasses(
            new Date(
              prevLastDay.getFullYear(),
              prevLastDay.getMonth(),
              prevLastDay.getDate() - i + 1,
            ),
          )}`}
          onClick={() =>
            handleDayClick(
              new Date(
                prevLastDay.getFullYear(),
                prevLastDay.getMonth(),
                prevLastDay.getDate() - i + 1,
              ),
            )
          }
        >
          <span>{prevLastDay.getDate() - i + 1}</span>
        </button>,
      );
    }

    for (let j = 1; j <= lastDay.getDate(); j += 1) {
      days.push(
        <button
          type="button"
          key={Math.random()}
          className={handleDayClasses(
            new Date(lastDay.getFullYear(), lastDay.getMonth(), j),
          )}
          onClick={() =>
            handleDayClick(
              new Date(lastDay.getFullYear(), lastDay.getMonth(), j),
            )
          }
        >
          <span>{j}</span>
        </button>,
      );
    }

    const nextDays = 7 - lastDayIndex - 1;

    for (let k = 1; k <= nextDays; k += 1) {
      days.push(
        <button
          type="button"
          key={Math.random()}
          className={`different-month ${handleDayClasses(
            new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, k),
          )}`}
          onClick={() =>
            handleDayClick(
              new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + 1,
                k,
              ),
            )
          }
        >
          <span>{k}</span>
        </button>,
      );
    }

    return days;
  }

  return (
    <Container {...props}>
      <header>
        <button type="button" onClick={handlePrevMonth}>
          <FaChevronLeft />
        </button>
        <span>
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <button type="button" onClick={handleNextMonth}>
          <FaChevronRight />
        </button>
      </header>
      <WeekDays>
        <span>Dom</span>
        <span>Seg</span>
        <span>Ter</span>
        <span>Qua</span>
        <span>Qui</span>
        <span>Sex</span>
        <span>Sab</span>
      </WeekDays>
      <Days>{renderDays()}</Days>
    </Container>
  );
};

export default Calendar;
